/* global Marionette, define, jQuery */
/* eslint-disable no-var, object-shorthand */
/**
 * Keyboard operation and auto-advance.
 *
 * One question is answered here: given what the visitor just did, is this step
 * finished? A pointer activation, an Enter, or a letter shortcut says yes; an
 * arrow key browsing a radio group says no, however many change events it
 * fires on the way. The rules that tell those apart are the whole of this
 * module, together with the letters the shortcut reads and the guard that
 * keeps them inside the choice list they belong to.
 *
 * They are worth reading without the render, step, and theming code around
 * them: every one of them is an accessibility decision, and each carries the
 * WCAG success criterion it answers to in its own comment.
 *
 * Like the step and theme modules, this reaches into no view. It is handed the
 * step it is operating on, the element that step is rendered as, and the two
 * things it may ask the flow to do — advance, or re-label its navigation.
 */
define( 'controllers/keyboard',[], function () {
	/** Field types that advance the step as soon as they are answered. */
	var autoAdvanceTypes = [
		'checkbox',
		'listradio',
		'listimage',
		'listselect',
		'listcountry',
		'liststate',
		'terms',
	];

	var controller = Marionette.Object.extend( {
		/**
		 * @param {Object}   options                   Controller options.
		 * @param {Function} options.activeStep        Returns the current
		 *                                             step's element.
		 * @param {Function} options.currentStep       Returns the current step.
		 * @param {Function} options.isStarted         Whether the flow has
		 *                                             left the welcome screen.
		 * @param {Function} options.advance           Leaves the current step.
		 * @param {Function} options.refreshNavigation Re-labels the back and
		 *                                             continue controls.
		 * @return {void}
		 */
		initialize: function ( options ) {
			this.activeStep = options.activeStep;
			this.currentStep = options.currentStep;
			this.isStarted = options.isStarted;
			this.advance = options.advance;
			this.refreshNavigation = options.refreshNavigation;
			// Not read until the visitor is moving through the flow, but
			// declared here on purpose: the one piece of state this module
			// keeps should be readable, not grepped for.
			this.holdAutoAdvance = false;
		},

		/**
		 * Move on by itself when a step is unambiguously finished.
		 *
		 * Answering a single choice is the whole answer, so waiting for a
		 * "continue" press would be busywork. Answering is only treated as
		 * finishing the step when all of these hold:
		 *
		 * - the flow has started and the step holds exactly one field, so a
		 *   step with more questions in it is never cut short;
		 * - the change came from inside the current step, since a change
		 *   event can bubble up from anywhere in the form;
		 * - the field's type auto-advances at all (single choices, terms) —
		 *   free text is excluded, because a visitor is not done typing just
		 *   because the input lost focus;
		 * - the control is not multi-select, and the field is not a
		 *   multi-select image list, since picking one of several intended
		 *   answers is not finishing;
		 * - the answer was committed rather than merely browsed. Arrow keys
		 *   are how a radio group, a select, or a country list is read from
		 *   the keyboard, and each move fires a change: advancing on those
		 *   would change context on input without advisement (WCAG 3.2.2)
		 *   and make the later options unreachable. Arrow browsing therefore
		 *   only sets the value, and the visitor commits with Enter or the
		 *   continue button; a pointer activation, a letter shortcut, and
		 *   Enter all still finish the step by themselves.
		 *
		 * When those do not hold the step stays put and only the navigation
		 * is refreshed, so "skip" can become "continue" now that there is an
		 * answer. The short delay before advancing lets the visitor see their
		 * selection register instead of the question vanishing under them.
		 *
		 * @param {Object} event The change event from a field element.
		 * @return {void}
		 */
		onChange: function ( event ) {
			var step = this.currentStep();
			if ( ! this.isStarted() || ! step || 1 !== step.fields.length ) {
				return;
			}

			var field = step.fields[ 0 ];
			var target = event.currentTarget;
			if (
				step.el &&
				target !== step.el &&
				! jQuery.contains( step.el, target )
			) {
				return;
			}

			if (
				-1 === autoAdvanceTypes.indexOf( field.get( 'type' ) ) ||
				target.multiple ||
				this.holdAutoAdvance ||
				( 'listimage' === field.get( 'type' ) &&
					1 === Number( field.get( 'allow_multi_select' ) ) )
			) {
				this.refreshNavigation();
				return;
			}

			setTimeout( this.advance, 180 );
		},

		/**
		 * Stop treating changes as keyboard browsing.
		 *
		 * A pointer activation is a commit, so it both ends any arrow-key
		 * browsing that came before it and lets its own change advance the
		 * step. Mousedown is used because it precedes the click that fires
		 * the change, and touch input raises it too.
		 *
		 * @return {void}
		 */
		onPointerDown: function () {
			this.holdAutoAdvance = false;
		},

		/**
		 * Commit the answer in a control and move on.
		 *
		 * The change is triggered first so a typed or arrow-browsed value is
		 * on the field model before validation reads it, and auto-advance is
		 * held while that happens so the step is left exactly once instead of
		 * both here and again from the change.
		 *
		 * @param {Object} target The control being committed.
		 * @return {void}
		 */
		commit: function ( target ) {
			this.holdAutoAdvance = true;
			jQuery( target ).trigger( 'change' );
			this.holdAutoAdvance = false;
			this.advance();
		},

		/**
		 * Tell an input that acts like a button apart from one that is typed in.
		 *
		 * Ninja Forms' submit control is an `input`, not a `button`, so the
		 * distinction cannot be made on tag name alone.
		 *
		 * @param {Object} target The control the key event landed on.
		 * @return {boolean} Whether the control is button-like.
		 */
		isButtonInput: function ( target ) {
			var type = ( target.type || '' ).toLowerCase();

			return (
				-1 !== [ 'submit', 'button', 'reset', 'image' ].indexOf( type )
			);
		},

		/**
		 * Let the whole flow be driven from the keyboard.
		 *
		 * Four interactions live here:
		 *
		 * - Any key first records whether it was an arrow key, which is what
		 *   onChange reads to tell browsing a choice list apart from
		 *   committing to one. Only arrow keys count as browsing: they are
		 *   the native way to move through a radio group or a select, and
		 *   they change the value on every move.
		 *
		 * - In a textarea, Enter inserts a newline as usual, so Cmd/Ctrl +
		 *   Enter is what continues.
		 * - In a single-line input or a select, Enter continues instead of
		 *   submitting the form early. A change is triggered first so the
		 *   typed value is committed to the field model before validation.
		 *   Button-like inputs are exempt: Ninja Forms renders its submit
		 *   control as an `input`, and the view re-parents that control into
		 *   the last step, so treating it as a typing context would swallow
		 *   the Enter that natively activates it and leave Send mouse-only.
		 * - On a choice step, a letter key selects the matching choice: the
		 *   shortcut letters are assigned in document order by
		 *   decorateShortcuts, and `event.which - 65` converts an
		 *   upper-case letter key code back to that index (A is 65, so A is
		 *   the first choice). Out-of-range letters are left alone, as are
		 *   typing contexts, so letters still type normally. The shortcut is
		 *   confined to the choice list itself — see withinChoiceList — so a
		 *   letter pressed while the close or navigation controls hold focus
		 *   does nothing at all.
		 *
		 * @param {Object} event The keydown event from inside the canvas.
		 * @return {void}
		 */
		onKeydown: function ( event ) {
			var target = event.target;
			var tagName = target.tagName.toLowerCase();

			this.holdAutoAdvance =
				-1 !== [ 37, 38, 39, 40 ].indexOf( event.which );

			if ( 'textarea' === tagName ) {
				if (
					13 === event.which &&
					( event.metaKey || event.ctrlKey )
				) {
					event.preventDefault();
					this.commit( target );
				}
				return;
			}

			if (
				13 === event.which &&
				( 'input' === tagName || 'select' === tagName ) &&
				! this.isButtonInput( target )
			) {
				event.preventDefault();
				this.commit( target );
				return;
			}

			if (
				'select' === tagName ||
				( 'input' === tagName &&
					-1 === [ 'radio', 'checkbox' ].indexOf( target.type ) )
			) {
				return;
			}

			var choices = this.activeStep().find( '[data-nf-cf-key] input' );

			if ( ! this.withinChoiceList( target, choices ) ) {
				return;
			}

			var index = event.which - 65;
			if ( index >= 0 && index < choices.length ) {
				event.preventDefault();
				choices.eq( index ).trigger( 'click' );
			}
		},

		/**
		 * Whether a key landed inside the choice list the shortcuts belong to.
		 *
		 * The keydown listener is bound to the whole canvas, so it also hears
		 * the close control and the Back and Continue buttons. A single
		 * character key that picks an answer and advances the flow from there
		 * is a shortcut with no way off it, and WCAG 2.1.4 asks for one of
		 * three: turning it off, remapping it, or confining it to the control
		 * it belongs to. This is the third. It matters most for a visitor
		 * using speech input, whose dictation routinely emits stray characters
		 * into whatever holds focus.
		 *
		 * The list is reached through the options rather than by selector,
		 * because core renders choices as list items for some field types and
		 * as bare labels for others: their shared parent is the only boundary
		 * that holds for both.
		 *
		 * @param {Object} target  The element the key event landed on.
		 * @param {Object} choices The current step's choice inputs.
		 * @return {boolean} True when the shortcut may fire.
		 */
		withinChoiceList: function ( target, choices ) {
			if ( ! choices.length ) {
				return false;
			}

			var list = choices
				.first()
				.closest( '[data-nf-cf-key]' )
				.parent()
				.get( 0 );

			return Boolean(
				list && ( list === target || jQuery.contains( list, target ) )
			);
		},

		/**
		 * Label the current step's choices A, B, C… for the keyboard.
		 *
		 * The letters are both the visible hint and the mapping onKeydown
		 * reads, so they are assigned in document order. Anything past the
		 * 26th choice gets no letter, since there are no more to give.
		 *
		 * @return {void}
		 */
		decorateShortcuts: function () {
			var choices = this.activeStep().find(
				'input[type="radio"], .listcheckbox-wrap input[type="checkbox"]'
			);
			choices.each( function ( index, input ) {
				if ( index > 25 ) {
					return;
				}
				var container = jQuery( input ).closest( 'li' );
				if ( ! container.length ) {
					container = jQuery( input ).closest( 'label' );
				}

				container.attr(
					'data-nf-cf-key',
					String.fromCharCode( 65 + index )
				);
			} );
		},
	} );

	return controller;
} );

